import { useMemo } from 'react'

import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedOfferingListingDetails,
  type OrchestratedAuctionListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { Breakpoints } from '@b-stock/fe-core/design-system'

import { WatchlistCTA } from '@/components/listings/watchlist'
import { FeatureFlags } from '@/helpers/featureFlags'
import { orchestratedListingToGTMItemInput } from '@/helpers/telemetry/gtmItemAdapters'
import { transformListingToGTMItem } from '@/helpers/telemetry/transformListingToGTMItem'
import { watchedStatusQuery } from '@/queries/watchlistQuery'

const DesktopWatchlistWrapper = styled.div`
  @media ${Breakpoints.max.small} {
    display: none;
  }
`

const MobileWatchlistWrapper = styled.div`
  @media ${Breakpoints.min.medium} {
    display: none;
  }
`

export const ListingWatchlistCTA = ({
  listing,
}: {
  listing:
    | OrchestratedOfferingListingDetails
    | OrchestratedAuctionListingDetails
}) => {
  const { formatMessage } = useIntl()
  const personId = useDecodedAccessToken()?.personId

  const enableLdpRedesign = useFlag(FeatureFlags.ENABLE_LDP_REDESIGN)

  const { data, isLoading, error } = watchedStatusQuery.useWithOpts(
    [{ personId, listingId: listing._id }],
    { enabled: !!personId }
  )

  const gtmItem = useMemo(
    () =>
      transformListingToGTMItem(orchestratedListingToGTMItemInput(listing), 0),
    [listing]
  )

  // if there's an error we don't show the watchlist cta
  if (error || isLoading) return null

  const listingEndTime =
    'auction' in listing && listing.auction
      ? (listing.auction.actualEndTime ?? listing.auction.initialEndTime)
      : undefined

  const ctaProps = {
    dateExpires: listing.dateVisibilityEnds,
    listingId: listing._id,
    listingTitle: listing.lot.title || formatMessage({ id: 'Common.unknown' }),
    isWatchlisted: data?.isWatchlisted || false,
    gtmItem,
    listingEndTime,
  }

  // In the LDP redesign the CTA is a button on desktop and an icon on mobile;
  // otherwise it's always an icon.
  if (!enableLdpRedesign) {
    return <WatchlistCTA {...ctaProps} />
  }

  return (
    <>
      <DesktopWatchlistWrapper>
        <WatchlistCTA {...ctaProps} variant="button" />
      </DesktopWatchlistWrapper>
      <MobileWatchlistWrapper>
        <WatchlistCTA {...ctaProps} />
      </MobileWatchlistWrapper>
    </>
  )
}
